// React imports
import React, { useMemo, useState, useRef } from 'react';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useDrag } from 'react-dnd'

// MUI imports
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { grey, yellow, red, green, blue } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faGlobe, faShip, faPlane, faTruck, faFile } from '@fortawesome/free-solid-svg-icons';
import { faCopy } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { calcOfferSum, calcSectionSum } from './utils/opportunity.js';
import currencyToText from './CurrencyToText.js';
import { getMultiplier } from './CalcWeight.js';
import SupplierLogos from '@monada-ai/monada-shipping-companies-logos';
import { normalizeProductType } from './dashboard/utils/product.js';

dayjs.extend(advancedFormat);

function locationLabel(loc, fallback) {
    if (!loc) return fallback;
    const label = (loc.id?.length <= 10 ? loc.id?.toUpperCase() : null) || loc.text || fallback;
    return label.length > 20 ? label.slice(0, 20) + '...' : label;
}

function RateCard(props) {
    const { organization, rate, onClick, exchangeRates, transitDatesIndex, standalone, standaloneWithDragging, countStr, onStartDragging, onEndDragging, opportunityProducts } = props;
    const [ dragging, setDragging ] = useState(false);
    const [ copied, setCopied ] = useState(false);
    const setDraggingRef = React.useRef(setDragging);
    const rateRef = useRef();
    const theme = useTheme();
    rateRef.current = rate;
    setDraggingRef.current = setDragging;

    const [, drag, preview] = useDrag(() => ({
        type: 'RateCard',
        item: () => {
            onStartDragging();
            setDraggingRef.current(true);
            // `_rfqSource` is a client-only display marker on RFQ "alternative cost" cards.
            // Strip it from the dragged payload so it never enters opportunity.buyingRates —
            // the server's strict schema rejects unknown keys on save.
            return { rate: _.omit(rateRef.current, '_rfqSource') };
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        }),
        end: () => {
            setDraggingRef.current(false);
            onEndDragging();
        }
    }))

    const allProducts = opportunityProducts || [ rate.product ];

    // For cost calculations, create a synthetic product that has the rate product's ID
    // (so field values match) but with aggregated weights from all opportunity products.
    const calcProducts = useMemo(() => {
        if (!opportunityProducts) return [ rate.product ];
        const rateType = normalizeProductType(rate.product.type);
        const isWM = rateType === 'LCL' || rateType.startsWith('By Volume or Weight') || rateType.startsWith('By Dimensions or Weight');
        if (isWM) {
            const wmProducts = opportunityProducts.filter(p => normalizeProductType(p.type) === rateType);
            if (wmProducts.length === 0) return [rate.product];
            const lclProduct = wmProducts.find(p => p.type === 'LCL' && p.calculations);
            if (lclProduct) {
                return [{ ...rate.product, calculations: lclProduct.calculations, kgOrLbs: lclProduct.kgOrLbs, cmOrInches: lclProduct.cmOrInches, densityRatio: rate.product.densityRatio, quantity: 1 }];
            }
            if (wmProducts.length === 1) return [{ ...wmProducts[0], id: rate.product.id }];
            const totalVolume = _.sumBy(wmProducts, p => {
                if (p.volume) return (p.volume || 0) * (p.quantity || 1);
                if (p.length && p.width && p.height) return (p.quantity || 1) * p.length * p.width * p.height / ((p.cmOrInches || 'cm') === 'cm' ? 1000000 : 1728);
                return 0;
            });
            const totalWeight = _.sumBy(wmProducts, p => (p.weight || 0) * (p.quantity || 1));
            return [{ ...rate.product, volume: totalVolume, weight: totalWeight, quantity: 1, length: undefined, width: undefined, height: undefined }];
        }
        // Air freight (unified)
        if (rateType === 'Air freight') {
            const afProduct = opportunityProducts?.find(p => p.type === 'Air freight' && p.calculations);
            if (afProduct) {
                return [{ ...rate.product, calculations: afProduct.calculations, kgOrLbs: afProduct.kgOrLbs, cmOrInches: afProduct.cmOrInches, roundChargeableWeight: afProduct.roundChargeableWeight, quantity: 1 }];
            }
        }

        const airProducts = opportunityProducts.filter(p => normalizeProductType(p.type) === rateType);
        if (airProducts.length === 0) return [ rate.product ];
        if (airProducts.length === 1) return [{ ...airProducts[0], id: rate.product.id }];
        const totalWeight = _.sumBy(airProducts, p => (p.weight || 0) * (p.quantity || 1));
        const totalCW = _.sum(airProducts.map(p => {
            const { multiplier } = getMultiplier({ product: p, transportationMethod: rate.transportationMethod, allProducts: airProducts, round: organization.airRoundChargeableWeight });
            return multiplier;
        }));
        return [{ ...rate.product, weight: totalWeight, quantity: 1, chargeableWeight: totalCW, volume: undefined, length: undefined, width: undefined, height: undefined }];
    }, [rate, opportunityProducts, organization]);

    const [totals, totalsForSection] = useMemo(() => {
        const transportationMethod = rate.transportationMethod;
        const totals = calcOfferSum({ offer: rate.offer, products: calcProducts, transportationMethod, exchangeRates: exchangeRates || { USD: 1 }, organization });
        const totalsForSection = _.fromPairs(rate.offer.sections.map(section => ([
            section.id,
            calcSectionSum({ section, sections: [section], products: calcProducts, transportationMethod, exchangeRates: exchangeRates || { USD: 1 }, organization }) ])));
        return [ totals, totalsForSection ];
    }, [rate, exchangeRates, calcProducts])
    const numStops = !rate.offer.transshipment ? 0 : (rate.offer.transshipment || '').split(',').length;

    const transitDates = (rate.offer.transitDates || [])[transitDatesIndex] || null;

    const type = (() => {
        const fraction = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });

        if (rate.product.type === 'Air Freight' || rate.product.type === 'Air freight') {
            const airProducts = allProducts.filter(p => p.type === 'Air Freight' || p.type === 'Air freight');
            const totalGrossWeight = _.sumBy(airProducts, p => (p.weight || 0) * (p.quantity || 1));
            const totalCW = _.sum(airProducts.map(p => getMultiplier({ product: p, transportationMethod: rate.transportationMethod, allProducts: airProducts, shorten: true, round: organization.airRoundChargeableWeight }).multiplier));
            const weightUnit = (airProducts[0] || rate.product).kgOrLbs === 'kg' ? 'kg' : 'lb';
            return `${fraction.format(totalGrossWeight).toLocaleString()} ${weightUnit} (gw) | ${fraction.format(totalCW).toLocaleString()} ${weightUnit} (cw) ${rate.product.dangerous ? ' (Dangerous)' : ''}`;
        }
        if (rate.product.type === 'By Volume or Weight (sea)' || rate.product.type === 'LCL' || rate.product.type?.startsWith('By Dimensions or Weight') || rate.product.type?.startsWith('By Volume or Weight')) {
            const { multiplier, multiplierText } = getMultiplier({ product: calcProducts[0] || rate.product, transportationMethod: rate.transportationMethod, allProducts: allProducts, shorten: true, round: organization.airRoundChargeableWeight });
            return `${fraction.format(multiplier).toLocaleString()} ${multiplierText} ${rate.product.dangerous ? ' (Dangerous)' : ''}`;
        }
        return `${rate.product.type}${rate.product.dangerous ? ' (Dangerous)' : ''}`;
    })();

    const showEtdNoTime = transitDates && rate.platform !== -1 && rate.transportationMethod === 'sea' && rate.type === 'spot';
    const showEtaNoTime = transitDates && rate.platform !== -1 && (transitDates.eta !== transitDates.etd) && rate.transportationMethod === 'sea' && rate.type === 'spot';
    const showQuotedOn = rate.platform !== -1 && !rate.attributes?.cargofiveRate && rate.transportationMethod === 'sea' && rate.type === 'spot';
    const showValidFromAndUntil = ((rate.platform === -1 && rate.type !== 'tariff') || rate.type === 'contract' || !!rate.attributes?.cargofiveRate);
    const showOnlyValidUntil = rate.type === 'tariff' && rate.offer.validUntil;;

    const showEtdTime = transitDates && rate.platform !== -1 && rate.transportationMethod === 'air';
    const showEtaTime = transitDates && rate.platform !== -1 && (transitDates.eta !== transitDates.etd) && rate.transportationMethod === 'air';

    if (!showEtdNoTime && !showEtdTime && !showEtaNoTime && !showEtaTime && transitDatesIndex > 0) return null;

    const serviceType = rate?.attributes?.cargofiveRate?.product_offer.service_type || 
        (rate.offer.transshipment ? 'Transshipment' : 'Direct');

    const bookingId = rate?.attributes?.cargofiveRate?.product_offer?.rate_details?.carrier_offer_id || null;
    const bookingLink = rate?.attributes?.cargofiveRate?.product_offer?.rate_details?.additional_data?.book_now_link || null;
    const spotOrContract = rate?.attributes?.cargofiveRate?.product_offer?.main_product_name || rate.type || '';
    const mainSourceType = rate?.attributes?.cargofiveRate?.main_source_type || null;
    
    // Determine if rate is from API: for cargofive use main_source_type, for others use platform !== -1
    const isApiSource = mainSourceType === 'api' || (rate.platform !== -1 && rate.platform !== undefined);

    // Calculate transit time
    const calculateTransitTime = () => {
        if (!transitDates || !transitDates.eta || !transitDates.etd) {
            return rate.offer.transitTime ? { days: rate.offer.transitTime, hours: null, minutes: null } : null;
        }
        
        if (rate.transportationMethod === 'air') {
            // For air, calculate hours and minutes
            const etdDate = new Date(transitDates.etd.replace(/[+-]\d\d:\d\d$/, '').replace(/Z$/, ''));
            const etaDate = new Date(transitDates.eta.replace(/[+-]\d\d:\d\d$/, '').replace(/Z$/, ''));
            const diffMs = etaDate - etdDate;
            const totalMinutes = Math.floor(diffMs / (1000 * 60));
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return { days: null, hours, minutes };
        } else {
            // For sea/land, calculate days
            const days = Math.ceil((new Date(transitDates.eta) - new Date(transitDates.etd)) / (1000 * 60 * 60 * 24));
            return { days, hours: null, minutes: null };
        }
    };
    
    const transitTime = calculateTransitTime();

    // Format dates as "Jan 19th 2026"
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return dayjs(dateString).format('MMM Do YYYY');
    };

    // Get time string for air transportation (e.g., "10:30 AM")
    const getTimeString = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString.replace(/[+-]\d\d:\d\d$/, '').replace(/Z$/, ''));
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    // Get supplier name
    const supplierName = rate.supplier?.organization || 'Carrier';

    // Get container/product type for tag
    const containerType = type || 'Container';

    return (
        <MenuItem 
            disableRipple={dragging || standaloneWithDragging}
            sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'flex-start',
                fontSize: '11px',
                background: theme.palette.mode === 'dark' ? grey[900] : 'white',
                width: standalone || standaloneWithDragging ? '100%' : '498px',
                border: standalone || standaloneWithDragging ? `1px solid ${theme.palette.border1}` : 'none',
                borderRadius: '8px',
                pointerEvents: standalone ? 'none' : 'auto',
                padding: '0px',
                margin: '0px',
                marginBottom: '12px',
                boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
            }}
            ref={drag}
            onClick={onClick}
        >
            <Box sx={{ display: 'flex', width: '100%', borderRadius: '8px', overflow: 'hidden' }} ref={preview}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, background: theme.palette.mode === 'dark' ? grey[900] : 'white' }}>
                    {/* Header: Logo + Supplier Name + Container Type Tag */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px',
                        borderRadius: '4px',
                        backgroundColor: theme.palette.mode === 'dark' ? grey[800] : blue[50],
                        maxWidth: '409px',
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                    }}>
                        <Box sx={{ flexShrink: 0 }}>
                            {rate.transportationMethod === 'sea' && (
                                <FontAwesomeIcon icon={faShip} style={{ color: theme.palette.mode === 'dark' ? grey[700] : grey[900], fontSize: '14px' }} />
                            )}
                            {rate.transportationMethod === 'air' && (
                                <FontAwesomeIcon icon={faPlane} style={{ color: theme.palette.mode === 'dark' ? grey[700] : grey[900], fontSize: '14px' }} />
                            )}
                            {rate.transportationMethod === 'land' && rate.source?.type !== 'unlocode' && (
                                <FontAwesomeIcon icon={faTruck} style={{ color: theme.palette.mode === 'dark' ? grey[700] : grey[900], fontSize: '14px' }} />
                            )}
                        </Box>
                        {spotOrContract && (
                            <Chip
                                label={spotOrContract?.toUpperCase() || ''}
                                sx={{
                                    flexShrink: 0,
                                    fontSize: '10px',
                                    height: '20px',
                                    backgroundColor: theme.palette.mode === 'dark' ? grey[700] : blue[100],
                                    color: theme.palette.mode === 'dark' ? 'white' : blue[900],
                                    fontWeight: 500,
                                }}
                            />
                        )}
                        <Chip
                            label={serviceType === 'Transshipment' && rate.offer.transshipment
                                ? `Transshipment (${rate.offer.transshipment.split(',').join(', ')})`
                                : serviceType}
                            sx={{
                                flexShrink: 0,
                                fontSize: '10px',
                                height: '20px',
                                backgroundColor: theme.palette.mode === 'dark' ? grey[700] : blue[100],
                                color: theme.palette.mode === 'dark' ? 'white' : blue[900],
                                fontWeight: 500,
                            }}
                        />
                        <Chip
                            label={containerType}
                            sx={{
                                flexShrink: 0,
                                fontSize: '10px',
                                height: '20px',
                                backgroundColor: theme.palette.mode === 'dark' ? grey[700] : blue[100],
                                color: theme.palette.mode === 'dark' ? 'white' : blue[900],
                                fontWeight: 500,
                            }}
                        />
                        {(rate.transportationMethod === 'sea' || rate.transportationMethod === 'air') && (
                            <Box sx={{ flexShrink: 0, marginLeft: '4px' }}>
                                <FontAwesomeIcon 
                                    icon={isApiSource ? faGlobe : faFile} 
                                    style={{ 
                                        color: theme.palette.mode === 'dark' ? grey[700] : blue[900], 
                                        fontSize: '14px' 
                                    }} 
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Route Section: Origin -> Transit Time -> Destination */}
                    <Box sx={{ width: '100%', padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {/* Top row: Origin and Destination locations */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingRight: '6px' }}>
                                <Box sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'white' : blue[900] }}>
                                    {locationLabel(rate.source, 'ORIGIN')}
                                </Box>
                                {(showEtdNoTime || showEtdTime) && transitDates && (
                                    <Box sx={{
                                        fontSize: '11px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}>
                                        <Box>{formatDate(transitDates.etd)}</Box>
                                        {showEtdTime && (
                                            <Box>{getTimeString(transitDates.etd)}</Box>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', marginRight: '0px' }}>
                                <Box sx={{ 
                                    width: '100%',
                                    height: '4px', 
                                    backgroundColor: theme.palette.mode === 'dark' ? grey[700] : blue[900],
                                }} />
                                
                                {/* Transit Time Badge centered on line */}
                                {transitTime && (
                                    <Box sx={{
                                        position: 'absolute',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: theme.palette.mode === 'dark' ? blue[800] : blue[900],
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {rate.transportationMethod === 'air' && transitTime.hours !== null
                                            ? `${transitTime.hours} hr ${transitTime.minutes} min`
                                            : transitTime.days !== null
                                            ? `${transitTime.days} DAYS`
                                            : ''}
                                        {/* Blue dot for cards with Transshipment */}
                                        {serviceType === 'Transshipment' && (rate.offer.transshipment || rate?.attributes?.cargofiveRate) && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: '50%',
                                                    transform: 'translateX(-50%) translateY(50%)',
                                                    width: '6px',
                                                    height: '6px',
                                                    borderRadius: '50%',
                                                    backgroundColor: theme.palette.mode === 'dark' ? blue[800] : blue[900],
                                                    zIndex: 1,
                                                }}
                                            />
                                        )}
                                    </Box>
                                )}
                                
                                {/* Blue dot for cards with Transshipment when there's no transit time badge */}
                                {!transitTime && serviceType === 'Transshipment' && (rate.offer.transshipment || rate?.attributes?.cargofiveRate) && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: '50%',
                                            top: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            backgroundColor: theme.palette.mode === 'dark' ? blue[800] : blue[900],
                                            zIndex: 1,
                                        }}
                                    />
                                )}
                            </Box>

                            <Box sx={{
                                flexShrink: 0,
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderLeft: `10px solid ${theme.palette.mode === 'dark' ? grey[700] : blue[900]}`,
                            }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingLeft: '6px' }}>
                                <Box sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'white' : blue[900] }}>
                                    {locationLabel(rate.destination, 'DESTINATION')}
                                </Box>
                                {(showEtaNoTime || showEtaTime) && transitDates && (
                                    <Box sx={{
                                        fontSize: '11px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                    }}>
                                        <Box>{formatDate(transitDates.eta)}</Box>
                                        {showEtaTime && (
                                            <Box>{getTimeString(transitDates.eta)}</Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Middle row: Transportation icon, line with transit badge, and arrow */}
                        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', margin: '4px 0' }}>
                            {/* Transportation icon */}

                            {/* Line with transit badge */}

                            {/* Arrow head */}
                        </Box>
                    </Box>

                    {/* Bottom Section: Service Type + Quoted Date */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '6px 16px',
                        backgroundColor: theme.palette.mode === 'dark' ? grey[800] : grey[100],
                    }}>
                        {showQuotedOn && (
                            <Box sx={{ fontSize: '11px', color: grey[600] }}>
                                Quoted: {dayjs(rate.created).format('MMM Do YYYY, h:mm A')}
                            </Box>
                        )}
                        {showValidFromAndUntil && (
                            <Box sx={{ fontSize: '11px', color: grey[600] }}>
                                Valid from: {dayjs(rate.offer.validFrom).format(rate.transportationMethod === 'air' ? 'MMM Do, h:mm A' : 'MMM Do')}
                                {rate.offer.validUntil && `-${dayjs(rate.offer.validUntil).format(rate.transportationMethod === 'air' ? 'MMM Do, h:mm A' : 'MMM Do')}`}
                            </Box>
                        )}

                        {bookingId && (
                            <Box sx={{ fontSize: '11px', color: grey[600], display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    window.open(bookingLink, '_blank');
                                }} sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
                                    Booking ID: {bookingId}
                                </Box>
                                <Tooltip 
                                    title={copied ? "Copied!" : "Copy Booking ID"}
                                    {...(copied ? { open: true, disableHoverListener: true } : {})}
                                    arrow
                                    PopperProps={{
                                        sx: { zIndex: 200000000 }
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevents parent click event
                                            e.preventDefault(); // Prevents default behavior
                                            navigator.clipboard.writeText(bookingId);
                                            setCopied(true);
                                            setTimeout(() => setCopied(false), 2000);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faCopy} fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Right Section: Price */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    padding: '16px',
                    borderLeft: `1px solid ${theme.palette.border1}`,
                    background: theme.palette.mode === 'dark' ? grey[900] : 'white',
                    minWidth: '90px',
                    width: '90px',
                    maxWidth: '90px',
                }}>
                    {/* Supplier Logo */}
                    <Box sx={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', whiteSpace: 'pre-wrap' }}>
                        <SupplierLogos size={SupplierLogos.size.LARGE} organization={SupplierLogos.organizations[supplierName.toUpperCase().replace(' ', '_').replace('-', '_')] || supplierName.toUpperCase()} />
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    {/* Price */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {Object.entries(totals?.max?.sum || {}).map(([currency, sum], index) => (
                            <Box key={currency} sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'white' : blue[900], lineHeight: 1.2 }}>
                                {index > 0 ? '+ ' : ''}{currencyToText(currency, true, sum.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }))}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </MenuItem>
    );
}

function shouldRenderRateCard({ rate, transitDatesIndex }) {
    const transitDates = (rate.offer.transitDates || [])[transitDatesIndex] || null;
    const showEtdNoTime = transitDates && rate.platform !== -1 && rate.transportationMethod === 'sea' && rate.type === 'spot';
    const showEtaNoTime = transitDates && rate.platform !== -1 && (transitDates.eta !== transitDates.etd) && rate.transportationMethod === 'sea' && rate.type === 'spot';
    const showEtdTime = transitDates && rate.platform !== -1 && rate.transportationMethod === 'air';
    const showEtaTime = transitDates && rate.platform !== -1 && (transitDates.eta !== transitDates.etd) && rate.transportationMethod === 'air';

    if (!showEtdNoTime && !showEtdTime && !showEtaNoTime && !showEtaTime && transitDatesIndex > 0) return false;
    return true;
}

export { RateCard, shouldRenderRateCard };

